import { useState } from 'react';
import LogoBlock from '../LogoBlock';
import { WhatsNewItem } from '../Footer';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from '~/components/ui/dropdown-menu';

import { integrationsList } from '~/routes/_public.chatgpt-for-microsoft-word';
import { Link } from '@remix-run/react';
import {
    BookUser,
    BriefcaseBusiness,
    ChevronDown,
    Clapperboard,
    Globe,
    Hash,
    Mail,
    Megaphone,
    MessageCircleHeart,
    MoveUpRight,
    SquarePen,
} from 'lucide-react';

import WordLogo from '~/assets/word.svg';
import OutlookLogo from '~/assets/outlook.svg';
import TeamsLogo from '~/assets/teams.svg';
import ExcelLogo from '~/assets/excel.svg';
import PowerPointLogo from '~/assets/powerpoint.svg';

function IntegrationsMenu({ className = '' }) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                className={`${className} text-base font-medium text-gray-900 transition-all duration-200 rounded focus:outline-none font-pj hover:text-opacity-50 focus:ring-1 focus:ring-gray-900 focus:ring-offset-2`}>
                <span>Apps &amp; Extensions</span>
                <ChevronDown className='inline ml-1 w-4 h-4' />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                {integrationsList.map((i, idx) => {
                    return (
                        <DropdownMenuItem key={`integration-${idx}`} asChild>
                            <Link
                                className='cursor-pointer text-base font-medium text-gray-900 transition-all duration-200 rounded focus:outline-none font-pj hover:text-opacity-50 focus:ring-1 focus:ring-gray-900 focus:ring-offset-2'
                                to={i.url}>
                                <img src={i.imgSrc} className='w-4 h-4' />
                                {i.name}
                            </Link>
                        </DropdownMenuItem>
                    );
                })}

                <DropdownMenuItem key={`integration-web`} asChild>
                    <Link
                        className='cursor-pointer text-base font-medium text-gray-900 transition-all duration-200 rounded focus:outline-none font-pj hover:text-opacity-50 focus:ring-1 focus:ring-gray-900 focus:ring-offset-2'
                        to={`/iframe`}>
                        <Globe className='mr-1' />
                        Web Version (No Install)
                    </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem key={`integration-other`} asChild>
                    <Link
                        className='cursor-pointer text-base font-medium text-gray-900 transition-all duration-200 rounded focus:outline-none font-pj hover:text-opacity-50 focus:ring-1 focus:ring-gray-900 focus:ring-offset-2'
                        to={'/integrations'}>
                        Browse all <MoveUpRight className='ml-1' />
                    </Link>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

const useCaseCategory = [
    {
        name: 'For Chat',
        url: '/tools/chat',
        iconEl: <MessageCircleHeart />,
    },
    {
        name: 'For Email',
        url: '/tools/mail',
        iconEl: <Mail />,
    },
    /*{
        name: 'For Sheets',
        url: '/chatgpt-for-microsoft-teams',
        iconEl: <TableProperties />,
    },*/
    {
        name: 'For Business',
        url: '/tools/business',
        iconEl: <BriefcaseBusiness />,
    },
    {
        name: 'For Content',
        url: '/tools/content',
        iconEl: <SquarePen />,
    },
    {
        name: 'For Social Media',
        url: '/tools/social%20media',
        iconEl: <Hash />,
    },
    {
        name: 'For Advertisement',
        url: '/tools/advertisement',
        iconEl: <Megaphone />,
    },
    {
        name: 'For Video',
        url: '/tools/video',
        iconEl: <Clapperboard />,
    },
];

const appCategory = [
    {
        name: 'For Word',
        url: '/tools/word',
        iconEl: <img src={WordLogo} className='w-4 h-4' />,
    },
    {
        name: 'For Teams',
        url: '/tools/teams',
        iconEl: <img src={TeamsLogo} className='w-4 h-4' />,
    },
    {
        name: 'For Excel',
        url: '/tools/excel',
        iconEl: <img src={ExcelLogo} className='w-4 h-4' />,
    },
    {
        name: 'For PowerPoint',
        url: '/tools/powerpoint',
        iconEl: <img src={PowerPointLogo} className='w-4 h-4' />,
    },
    {
        name: 'For Outlook',
        url: '/tools/outlook',
        iconEl: <img src={OutlookLogo} className='w-4 h-4' />,
    },
];

const roleCategory = [
    {
        name: 'For HR',
        url: '/chatgpt-for-microsoft-word',
        iconEl: <BookUser />,
    },
    {
        name: 'For Marketers',
        url: '/chatgpt-for-microsoft-excel',
        iconEl: <BookUser />,
    },
    {
        name: 'For Managers',
        url: '/chatgpt-for-microsoft-powerpoint',
        iconEl: <BookUser />,
    },
    {
        name: 'For Executives',
        url: '/chatgpt-for-microsoft-outlook',
        iconEl: <BookUser />,
    },
];

function FreeAiToolsMenu({ className = '' }) {
    function toMenu(list: any[], catName: string) {
        return list.map((i, idx) => {
            return (
                <DropdownMenuItem key={`integration-${catName}-${idx}`} asChild>
                    <Link
                        className='cursor-pointer text-base font-medium text-gray-900 transition-all duration-200 rounded focus:outline-none font-pj hover:text-opacity-50 focus:ring-1 focus:ring-gray-900 focus:ring-offset-2'
                        to={i.url}>
                        {i.iconEl}
                        {i.name}
                    </Link>
                </DropdownMenuItem>
            );
        });
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                className={`${className} text-base font-medium text-gray-900 transition-all duration-200 rounded focus:outline-none font-pj hover:text-opacity-50 focus:ring-1 focus:ring-gray-900 focus:ring-offset-2`}>
                <span>Free AI Tools</span>
                <ChevronDown className='inline ml-1 w-4 h-4' />
            </DropdownMenuTrigger>
            <DropdownMenuContent>
                <DropdownMenuLabel>By App</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {toMenu(appCategory, 'app')}
                <DropdownMenuLabel>By Use Case</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {toMenu(useCaseCategory, 'use-case')}
                {/*
                <DropdownMenuLabel>By Role</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {toMenu(roleCategory, 'role')}
                */}
                <DropdownMenuSeparator />
                <DropdownMenuItem key={`integration-other`} asChild>
                    <Link
                        className='cursor-pointer text-base font-medium text-gray-900 transition-all duration-200 rounded focus:outline-none font-pj hover:text-opacity-50 focus:ring-1 focus:ring-gray-900 focus:ring-offset-2'
                        to={'/tools'}>
                        Browse all <MoveUpRight className='ml-1' />
                    </Link>
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export const Header = () => {
    const [isOpen, setOpen] = useState(false);

    return (
        <header className='relative z-10 py-4 md:py-6'>
            <div className='container px-4 mx-auto sm:px-6 lg:px-8'>
                <div className='relative flex items-center justify-between'>
                    <div className='flex-shrink-0'>
                        <a
                            href='/'
                            title=''
                            className='flex items-center justify-center rounded outline-none focus:ring-1 focus:ring-gray-900 focus:ring-offset-2'>
                            <LogoBlock />
                        </a>
                    </div>

                    <div className='flex lg:hidden'>
                        <button
                            type='button'
                            className='text-gray-900'
                            onClick={() => setOpen(!isOpen)}>
                            <span
                                aria-hidden='true'
                                className={!isOpen ? 'block' : 'hidden'}>
                                <svg
                                    className='w-7 h-7'
                                    xmlns='http://www.w3.org/2000/svg'
                                    fill='none'
                                    viewBox='0 0 24 24'
                                    stroke='currentColor'>
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='1.5'
                                        d='M4 6h16M4 12h16M4 18h16'
                                    />
                                </svg>
                            </span>

                            <span
                                aria-hidden='true'
                                className={isOpen ? 'block' : 'hidden'}>
                                <svg
                                    className='w-7 h-7'
                                    xmlns='http://www.w3.org/2000/svg'
                                    fill='none'
                                    viewBox='0 0 24 24'
                                    stroke='currentColor'>
                                    <path
                                        strokeLinecap='round'
                                        strokeLinejoin='round'
                                        strokeWidth='2'
                                        d='M6 18L18 6M6 6l12 12'
                                    />
                                </svg>
                            </span>
                        </button>
                    </div>
                    {/* Desktop Menu */}
                    <div className='hidden lg:flex lg:items-center lg:justify-end lg:space-x-10 '>
                        <FreeAiToolsMenu />
                        <a
                            href='/pricing'
                            title=''
                            className='text-base font-medium text-gray-900 transition-all duration-200 rounded focus:outline-none font-pj hover:text-opacity-50 focus:ring-1 focus:ring-gray-900 focus:ring-offset-2'>
                            {' '}
                            Pricing{' '}
                        </a>
                        <IntegrationsMenu />
                        <WhatsNewItem className='text-base font-medium text-gray-900 transition-all duration-200 rounded focus:outline-none font-pj hover:text-opacity-50 focus:ring-1 focus:ring-gray-900 focus:ring-offset-2' />
                    </div>
                </div>
                {/* Mobile Menu */}
                <nav className={isOpen ? 'block' : 'hidden'}>
                    <div className='px-1 py-8'>
                        <div className='grid gap-y-7 items-start'>
                            <FreeAiToolsMenu className='flex items-center justify-start' />
                            <a
                                href='/pricing'
                                title=''
                                className='flex items-center p-3 -m-3 text-base font-medium text-gray-900 transition-all duration-200 rounded hover:bg-gray-50 focus:outline-none font-pj focus:ring-1 focus:ring-gray-900 focus:ring-offset-2'>
                                {' '}
                                Pricing{' '}
                            </a>
                            <IntegrationsMenu className='flex items-center justify-start' />
                            <WhatsNewItem className='flex items-center p-3 -m-3 text-base font-medium text-gray-900 transition-all duration-200 rounded hover:bg-gray-50 focus:outline-none font-pj focus:ring-1 focus:ring-gray-900 focus:ring-offset-2' />
                        </div>
                    </div>
                </nav>
            </div>
        </header>
    );
};

export default Header;
